import moment from 'moment'

import { ALL_TIME_START_DATE } from 'components/date-range-picker/date-defaults'
import type { DashboardFilterStateV2 } from 'features/dashboards-v2-1/types/filters-v2'
import { emptyDashboardFilterStateV2 } from 'features/dashboards-v2-1/types/filters-v2'
import type {
  DateRange,
  FilterOption,
  FilterOptionDate,
  FilterOptionRange,
} from 'features/filters/types'
import {
  formatDateToUTCEndOfDay,
  formatDateToUTCMidnight,
  HardcodedFilterOptionsEnum,
} from 'features/filters/utils/helpers'
import {
  DEFAULT_TRAILING_DAYS,
  TimeFrame,
} from 'src/features/dashboards-v2-1/features/filter-header/constants'
import type { DashboardResponse } from 'src/features/dashboards-v2-1/types'
import type { DateWindows } from 'types/project'

/**
 * Set (or replace) a dashboard-scoped filter state's date range. Returns a
 * new `DashboardFilterStateV2` reference. The typed successor to v1's
 * "replace the create_time entry in `values[]`" — see `dashboards-v2-1/AGENTS.md`.
 */
export function setDateRange(
  state: DashboardFilterStateV2,
  min: number,
  max: number,
): DashboardFilterStateV2 {
  return { ...state, dateRange: { startDate: min, endDate: max } }
}

/**
 * Clear a dashboard-scoped filter state's own date range — "synced with the
 * parent" at the item level. Returns a new `DashboardFilterStateV2` reference.
 */
export function clearDateRange(
  state: DashboardFilterStateV2,
): DashboardFilterStateV2 {
  return { ...state, dateRange: null }
}

/**
 * Return true when the filter state's own date range matches the supplied
 * min/max. A `null` date range (inherit) never matches a concrete min/max.
 */
export function dateRangeMatches(
  state: DashboardFilterStateV2,
  min: number,
  max: number,
): boolean {
  const dr = state.dateRange
  return !!dr && dr.startDate === min && dr.endDate === max
}

/**
 * Find the create_time filter option in a list of filter options (applied
 * or default), regardless of its concrete FilterOption subtype.
 */
function findCreateTimeOption(
  filterList: FilterOption[],
): FilterOption | undefined {
  return filterList?.find(
    (filterOption) =>
      filterOption.field === HardcodedFilterOptionsEnum.CREATE_TIME,
  )
}

/**
 * Effective create_time bounds for the dashboard header's date picker:
 * min/max are each independently taken from the applied create_time filter,
 * falling back to the project's default create_time range, then to 0.
 */
export function resolveHeaderDateBounds(
  filters: DashboardFilterStateV2,
  defaultFilterList: FilterOption[],
): { min: number; max: number } {
  const fallback = findCreateTimeOption(defaultFilterList) as
    | FilterOptionRange
    | undefined
  return {
    min: filters.dateRange?.startDate ?? fallback?.min ?? 0,
    max: filters.dateRange?.endDate ?? fallback?.max ?? 0,
  }
}

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
export const generateDates = (
  filterList: FilterOption[],
): Record<string, DateRange[]> => {
  const datesObj: Record<string, DateRange[]> = {
    [TimeFrame.WEEK]: [],
    [TimeFrame.MONTH]: [],
    [TimeFrame.QUARTER]: [],
    [TimeFrame.HALF]: [],
    [TimeFrame.YEAR]: [],
    [TimeFrame.YTD]: [],
    [TimeFrame.TRAILING]: [],
    allDates: [],
  }

  const option = findCreateTimeOption(filterList) as FilterOptionDate

  // In the past, date ranges used the most recent review timestamp as the max value
  // (this comes back as the 'max' property of the "create_time" filter option type)
  // the new date select dropdowns operate with the current date being the max timestamp instead
  // the constructed date windows will now reflect that change as well, comment left for posterity
  const currentDate = new Date() // option ? new Date(option?.max * 1000) : new Date()
  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth()

  const dataMinDate = option
    ? new Date(option?.min * 1000)
    : new Date(2019, 0, 1)
  const beginningDate =
    dataMinDate > ALL_TIME_START_DATE ? dataMinDate : ALL_TIME_START_DATE
  const beginningYear = beginningDate.getFullYear()
  const beginningMonth = beginningDate.getMonth()

  // TODO times should only go up to most recently available data, from earliest available data
  // TODO need to load in selected date range from saved dashboard

  const getTimeInSec = (date: Date): number => {
    return Math.floor(date.getTime() / 1000)
  }
  // Generate Sunday-aligned weeks from beginningDate to currentDate
  {
    // Find the first Sunday on or before beginningDate
    const firstSunday = new Date(beginningDate)
    firstSunday.setDate(firstSunday.getDate() - firstSunday.getDay())

    const startDate = new Date(firstSunday)
    while (true) {
      const endDate = new Date(startDate)
      endDate.setDate(endDate.getDate() + 6)
      if (endDate > currentDate) break
      const date = {
        label: `${monthNames[startDate.getMonth()]} ${startDate.getDate()} - ${
          monthNames[endDate.getMonth()]
        } ${endDate.getDate()}, ${endDate.getFullYear()}`,
        value: JSON.stringify({
          min: formatDateToUTCMidnight(
            moment.unix(getTimeInSec(startDate)),
          ).unix(),
          max: formatDateToUTCEndOfDay(
            moment.unix(getTimeInSec(endDate)),
          ).unix(),
        }),
      }
      datesObj[TimeFrame.WEEK]!.push(date)
      datesObj['allDates']!.push({ ...date, type: TimeFrame.WEEK })
      startDate.setDate(startDate.getDate() + 7)
    }
  }
  for (let year = beginningYear; year <= currentYear; year++) {
    const maxMonth = year === currentYear ? currentMonth : 11
    const minMonth = year === beginningYear ? beginningMonth : 0
    for (let month = minMonth; month <= maxMonth; month++) {
      const startDate = new Date(year, month, 1)
      const endDate = new Date(year, month + 1, 0)
      const date = {
        label: `${monthNames[month]} ${year}`,
        value: JSON.stringify({
          min: formatDateToUTCMidnight(
            moment.unix(getTimeInSec(startDate)),
          ).unix(),
          max: formatDateToUTCEndOfDay(
            moment.unix(getTimeInSec(endDate)),
          ).unix(),
        }),
      }
      datesObj[TimeFrame.MONTH]!.push(date)
      datesObj['allDates']!.push({ ...date, type: TimeFrame.MONTH })
    }
  }
  for (let year = beginningYear; year <= currentYear; year++) {
    const maxQuarter =
      year === currentYear ? Math.floor(currentMonth / 3) + 1 : 4
    const minQuarter =
      year === beginningYear ? Math.floor(beginningMonth / 3) : 1
    for (let quarter = minQuarter; quarter <= maxQuarter; quarter++) {
      const startDate = new Date(year, (quarter - 1) * 3, 1)
      const endDate = new Date(year, quarter * 3, 0)
      const date = {
        label: `Q${quarter} ${year}`,
        value: JSON.stringify({
          min: formatDateToUTCMidnight(
            moment.unix(getTimeInSec(startDate)),
          ).unix(),
          max: formatDateToUTCEndOfDay(
            moment.unix(getTimeInSec(endDate)),
          ).unix(),
        }),
      }
      datesObj[TimeFrame.QUARTER]!.push(date)
      datesObj['allDates']!.push({ ...date, type: TimeFrame.QUARTER })
    }
  }
  for (let year = beginningYear; year <= currentYear; year++) {
    const startDate1 = new Date(year, 0, 1)
    const endDate1 = new Date(year, 5, 30)
    if (startDate1 <= currentDate) {
      const date = {
        label: `H1 ${year}`,
        value: JSON.stringify({
          min: formatDateToUTCMidnight(
            moment.unix(getTimeInSec(startDate1)),
          ).unix(),
          max: formatDateToUTCEndOfDay(
            moment.unix(getTimeInSec(endDate1)),
          ).unix(),
        }),
      }
      datesObj[TimeFrame.HALF]!.push(date)
      datesObj['allDates']!.push({ ...date, type: TimeFrame.HALF })
    }
    const startDate2 = new Date(year, 6, 1)
    const endDate2 = new Date(year, 11, 31)
    if (startDate2 <= currentDate) {
      const date = {
        label: `H2 ${year}`,
        value: JSON.stringify({
          min: formatDateToUTCMidnight(
            moment.unix(getTimeInSec(startDate2)),
          ).unix(),
          max: formatDateToUTCEndOfDay(
            moment.unix(getTimeInSec(endDate2)),
          ).unix(),
        }),
      }
      datesObj[TimeFrame.HALF]!.push(date)
      datesObj['allDates']!.push({ ...date, type: TimeFrame.HALF })
    }
  }
  for (let year = beginningYear; year <= currentYear; year++) {
    const startDate = new Date(year, 0, 1)
    const endDate =
      year === currentYear
        ? new Date(year, currentMonth, currentDate.getDate())
        : new Date(year, 11, 31)
    const date = {
      label: `YTD ${year}`,
      value: JSON.stringify({
        min: formatDateToUTCMidnight(
          moment.unix(getTimeInSec(startDate)),
        ).unix(),
        max: formatDateToUTCEndOfDay(moment.unix(getTimeInSec(endDate))).unix(),
      }),
    }
    datesObj[TimeFrame.YTD]!.push(date)
    datesObj['allDates']!.push({ ...date, type: TimeFrame.YTD })
  }
  for (let year = beginningYear; year <= currentYear; year++) {
    const startDate = new Date(year, 0, 1)
    const endDate = new Date(year, 11, 31)
    const date = {
      label: `${year}`,
      value: JSON.stringify({
        min: formatDateToUTCMidnight(
          moment.unix(getTimeInSec(startDate)),
        ).unix(),
        max: formatDateToUTCEndOfDay(moment.unix(getTimeInSec(endDate))).unix(),
      }),
    }
    datesObj[TimeFrame.YEAR]!.push(date)
    datesObj['allDates']!.push({ ...date, type: TimeFrame.YEAR })
  }
  const now = moment()
  const trailingDates: DateRange[] = [
    // Inserted first (not appended) so it lands LAST after this function's
    // trailing `.reverse()` — `resolveCurrentDateRange`'s Trailing-branch
    // fallback reads `resolutionDates[1]` for an unmatched daysCount, and
    // that must keep meaning "3 Months" regardless of this new bucket.
    {
      label: '7 Days',
      value: JSON.stringify({
        min: formatDateToUTCMidnight(moment(now).subtract(7, 'days')).unix(),
        max: formatDateToUTCEndOfDay(now).unix(),
      }),
      type: TimeFrame.TRAILING,
      daysCount: Math.round(moment.duration(7, 'days').asDays()),
    },
    {
      label: '2 Years',
      value: JSON.stringify({
        min: formatDateToUTCMidnight(moment(now).subtract(2, 'years')).unix(),
        max: formatDateToUTCEndOfDay(now).unix(),
      }),
      type: TimeFrame.TRAILING,
      daysCount: Math.round(moment.duration(2, 'years').asDays()),
    },
    {
      label: '12 Months',
      value: JSON.stringify({
        min: formatDateToUTCMidnight(moment(now).subtract(1, 'year')).unix(),
        max: formatDateToUTCEndOfDay(now).unix(),
      }),
      type: TimeFrame.TRAILING,
      daysCount: Math.round(moment.duration(1, 'year').asDays()),
    },
    {
      label: '6 Months',
      value: JSON.stringify({
        min: formatDateToUTCMidnight(moment(now).subtract(6, 'months')).unix(),
        max: formatDateToUTCEndOfDay(now).unix(),
      }),
      type: TimeFrame.TRAILING,
      daysCount: Math.round(moment.duration(6, 'months').asDays()),
    },
    {
      label: '3 Months',
      value: JSON.stringify({
        min: formatDateToUTCMidnight(moment(now).subtract(3, 'months')).unix(),
        max: formatDateToUTCEndOfDay(now).unix(),
      }),
      type: TimeFrame.TRAILING,
      daysCount: Math.round(moment.duration(3, 'months').asDays()),
    },
    {
      label: '1 Month',
      value: JSON.stringify({
        min: formatDateToUTCMidnight(moment(now).subtract(1, 'month')).unix(),
        max: formatDateToUTCEndOfDay(now).unix(),
      }),
      type: TimeFrame.TRAILING,
      daysCount: Math.round(moment.duration(1, 'month').asDays()),
    },
  ]
  datesObj[TimeFrame.TRAILING] = trailingDates
  trailingDates.forEach((date) => {
    datesObj['allDates']!.push({ ...date, type: TimeFrame.TRAILING })
  })
  Object.keys(datesObj).forEach((key) => {
    datesObj[key]!.reverse()
  })
  return datesObj
}

export const getInitialFilters = (
  dateWindows: DateWindows,
): DashboardFilterStateV2 => {
  const trailingWindow = dateWindows?.[TimeFrame.TRAILING]?.find(
    (el) => el.daysCount === DEFAULT_TRAILING_DAYS,
  )?.value
  if (!trailingWindow) return { ...emptyDashboardFilterStateV2 }

  const trailing30 = JSON.parse(trailingWindow)
  if (!trailing30.min || !trailing30.max) {
    return { ...emptyDashboardFilterStateV2 }
  }

  return setDateRange(
    emptyDashboardFilterStateV2,
    trailing30.min,
    trailing30.max,
  )
}

/**
 * Resolve the current date range for a given time resolution from dateWindows.
 * Used by both the global filter hydration and per-chart "Most Recent" hydration
 * to avoid duplicating the resolution logic.
 */
export function resolveCurrentDateRange(
  dateWindows: DateWindows,
  timeResolution: string,
  trailingDaysCount?: number,
): { min: number; max: number } | undefined {
  const resolutionDates = dateWindows[timeResolution]
  if (!resolutionDates?.length) return undefined
  if (timeResolution === 'YTD') {
    const val = resolutionDates[0]?.value
    return val ? JSON.parse(val) : undefined
  }
  if (timeResolution === 'Trailing') {
    const match = resolutionDates.find(
      (el) => el.daysCount === (trailingDaysCount ?? DEFAULT_TRAILING_DAYS),
    )
    const fallback = resolutionDates[1]?.value
    return match
      ? JSON.parse(match.value)
      : fallback
        ? JSON.parse(fallback)
        : undefined
  }
  // For Week, the generation loop already excludes partial weeks (stops when
  // endDate > now), so index 0 is the most recent *complete* week. For other
  // resolutions (Month, Quarter, etc.) index 0 may be a partial period, so
  // index 1 gives the most recent complete period.
  const idx = timeResolution === TimeFrame.WEEK ? 0 : 1
  const val = resolutionDates[idx]?.value
  return val ? JSON.parse(val) : undefined
}

/**
 * The range to write for a date-picker selection: when `mostRecentResolution`
 * is given, prefers the calendar-based `dateWindows` range so
 * `useDashboardFilterHeader`'s hydration effect's `createTimeMatches` guard
 * passes immediately instead of firing a second `setFilters` a tick later
 * once it notices the dates just written don't match its own calendar-based
 * math (every tile on the dashboard would otherwise refetch twice instead of
 * once — see dashboards-v2-1 PROD-5610). Otherwise (or if `dateWindows`
 * hasn't resolved that window yet, e.g. still loading), falls back to the
 * passed exact-day dates.
 */
export function resolveMostRecentWriteRange(
  mostRecentResolution: { resolution: string; trailingDays?: number } | null,
  dateWindows: DateWindows,
  fallbackStartDate: Date,
  fallbackEndDate: Date,
): { min: number; max: number } {
  const resolved = mostRecentResolution
    ? resolveCurrentDateRange(
        dateWindows,
        mostRecentResolution.resolution,
        mostRecentResolution.trailingDays,
      )
    : undefined
  return {
    min: resolved?.min ?? Math.floor(fallbackStartDate.getTime() / 1000),
    max: resolved?.max ?? Math.floor(fallbackEndDate.getTime() / 1000),
  }
}

/**
 * Hydrate saved filters with the current date range from dateWindows.
 * For isCurrent dashboards, the saved dates are stale (they were the trailing
 * dates at save time). This function replaces them with the current trailing
 * dates from the freshly-computed dateWindows.
 */
export function getHydratedFilters(
  data: DashboardResponse | undefined,
  dateWindows: DateWindows,
): DashboardFilterStateV2 {
  const settings = data?.dashboard?.dashboard_settings
  const savedFilters = settings?.filters ?? getInitialFilters(dateWindows)
  if (!settings?.isCurrent) return savedFilters
  // Default to 'Trailing' to match useDashboardViewDateState's default.
  // Without this, dashboards saved before timeResolution was added would
  // skip hydration and mount charts with stale dates.
  const timeResolution = settings.timeResolution || 'Trailing'
  const trailingDaysCount = settings.trailingDaysCount
  const dateFilter = resolveCurrentDateRange(
    dateWindows,
    timeResolution,
    trailingDaysCount,
  )
  if (!dateFilter) return savedFilters
  return setDateRange(savedFilters, dateFilter.min, dateFilter.max)
}
